'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { paymentMethodsService } from '@/features/payment/api/payment-methods';
import type { PaymentMethodData } from '@/types/stripe';
import { useAuthStore } from '@/features/auth';
import { ApiClientError } from '@/shared/lib/api';
import { createLogger } from '@/shared/lib/logger';
const logger = createLogger('ExpiredPaymentMethods');

// Pages that should check for expired payment methods
const PAYMENT_RELATED_PATHS = [
  '/settings',
  '/settings/payments',
  '/payment-methods',
  '/account/plans',
  '/tokens',
];

export function useExpiredPaymentMethods(enabled = true) {
  const [expiredMethods, setExpiredMethods] = useState<PaymentMethodData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const pathname = usePathname();
  const fetchingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Check if current path should check for expired methods
  const shouldCheck =
    enabled && PAYMENT_RELATED_PATHS.some((path) => pathname?.startsWith(path));

  const checkExpiredMethods = useCallback(async () => {
    // Don't fetch if not on a payment-related page
    if (!shouldCheck) {
      setIsLoading(false);
      setExpiredMethods([]);
      return;
    }

    // Don't fetch if not authenticated
    if (!isAuthenticated || !isInitialized) {
      setIsLoading(false);
      return;
    }

    // Prevent duplicate requests
    if (fetchingRef.current) {
      logger.debug('Request already in progress, skipping duplicate');
      return;
    }

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    fetchingRef.current = true;
    abortControllerRef.current = new AbortController();

    try {
      const methods = await paymentMethodsService.list();
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1; // JavaScript months are 0-indexed

      const expired = methods.filter((method) => {
        if (method.provider !== 'stripe') return false;

        const expYear = method.card_exp_year;
        const expMonth = method.card_exp_month;

        // Card is expired if:
        // - Year is in the past, OR
        // - Year is current year AND month is in the past
        return (
          expYear < currentYear ||
          (expYear === currentYear && expMonth < currentMonth)
        );
      });

      setExpiredMethods(expired);
    } catch (error) {
      // Ignore aborted requests
      if (error instanceof Error && error.name === 'AbortError') {
        logger.debug('Request was aborted');
        return;
      }
      // Only log errors that aren't authentication related
      if (error instanceof ApiClientError && error.status === 401) {
        // Silently ignore 401 errors - user is not authenticated
        setExpiredMethods([]);
      } else {
        logger.error('Failed to check expired payment methods:', error);
      }
    } finally {
      fetchingRef.current = false;
      setIsLoading(false);
    }
  }, [isAuthenticated, isInitialized, shouldCheck]);

  useEffect(() => {
    // Only check when authentication is initialized
    if (isInitialized) {
      checkExpiredMethods();
    }

    // Cleanup function to cancel in-flight requests
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      // Don't reset fetchingRef here - let it be reset in the finally block
      // This prevents duplicate requests in React StrictMode
    };
  }, [checkExpiredMethods]);

  return {
    expiredMethods,
    isLoading,
    refetch: checkExpiredMethods,
  };
}
